import {
  type CreditScore,
  type CreditScoreDetail,
  type CreditScoringDashboardMetrics,
  type BureauAlert,
  type ScoreHistoryPoint,
} from '@/types/credit-scoring';

// Helper function to generate score history
const generateScoreHistory = (
  currentInternal: number,
  currentBureau: number,
  trend: 'improving' | 'declining' | 'stable'
): ScoreHistoryPoint[] => {
  const history: ScoreHistoryPoint[] = [];
  const now = new Date();
  
  for (let i = 11; i >= 0; i--) {
    const date = new Date(now);
    date.setMonth(date.getMonth() - i);
    
    let internalScore = currentInternal;
    let bureauScore = currentBureau;
    
    if (trend === 'declining') {
      internalScore = currentInternal + (i * 5);
      bureauScore = currentBureau + (i * 6);
    } else if (trend === 'improving') {
      internalScore = currentInternal - (i * 4);
      bureauScore = currentBureau - (i * 5);
    } else {
      internalScore = currentInternal + (Math.random() * 20 - 10);
      bureauScore = currentBureau + (Math.random() * 20 - 10);
    }
    
    history.push({
      date,
      internalScore: Math.round(internalScore),
      bureauScore: Math.round(bureauScore),
    });
  }
  
  return history;
};

// Scenario 1: Excellent Customer
const customer1: CreditScoreDetail = {
  customerId: 'CUST-001',
  customerName: 'Acme Corporation',
  accountNumber: 'ACC-10001',
  internalScore: 780,
  bureauScore: 795,
  scoreVariance: 15,
  riskLevel: 'Low',
  creditLimit: 500000,
  outstandingBalance: 250000,
  utilizationRate: 50,
  paymentBehaviorScore: 95,
  dso: 28,
  bureauLastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
  lastReviewDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000), // 15 days ago
  bureauSyncStatus: 'synced',
  hasBureauAlerts: false,
  bureauAlertCount: 0,
  scoreHistory: generateScoreHistory(780, 795, 'stable'),
  scoreComponents: {
    paymentHistory: {
      weight: 40,
      score: 98,
      onTimeRate: 100,
      latePaymentFrequency: 0,
      agingBreakdown: {
        current: 100,
        days30: 0,
        days60: 0,
        days90: 0,
        days90Plus: 0,
      },
    },
    creditUtilization: {
      weight: 30,
      score: 85,
      currentUtilization: 50,
      utilizationTrend: 'stable',
    },
    creditHistoryLength: {
      weight: 15,
      score: 92,
      monthsActive: 84,
    },
    paymentBehaviorPatterns: {
      weight: 15,
      score: 90,
      consistency: 95,
      seasonalPatterns: false,
      disputeFrequency: 0,
    },
  },
  bureauIntelligence: {
    bureauCreditScore: 795,
    creditRiskClass: 1,
    paymentIndex: 95,
    daysBeyondTerms: 0,
    publicRecords: [],
    creditInquiries: [
      {
        id: 'INQ-001',
        date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
        requestingCreditor: 'Regional Bank',
        inquiryType: 'soft',
      },
    ],
    tradeLineSummary: {
      activeAccounts: 8,
      totalCreditExposure: 2500000,
      averagePaymentDays: 28,
      accountsInGoodStanding: 8,
      accountsWithLatePayments: 0,
    },
    paymentBehaviorTrends: 'Consistently pays within terms. Excellent payment history across all trade lines.',
    negativePaymentExperiences: [],
  },
  bureauEvents: [
    {
      id: 'EVT-001',
      date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
      type: 'payment_improvement',
      severity: 'positive',
      title: 'Perfect Payment Streak',
      description: '90 consecutive days of on-time payments',
      impact: 'Positive impact on credit score',
    },
  ],
  financialMetrics: {
    totalCreditLimit: 500000,
    bureauTotalCreditExposure: 2500000,
    currentOutstandingBalance: 250000,
    availableCredit: 250000,
    averageDaysToPay: 28,
    bureauIndustryAverageDays: 45,
    largestInvoiceOutstanding: 75000,
    totalInvoicesOpen: 12,
    totalInvoicesOverdue: 0,
    bureauTradeLineCount: 8,
  },
  riskIndicators: {
    internalRedFlags: [],
    bureauRedFlags: [],
    combinedRiskScore: 15,
    positiveIndicators: [
      'Perfect payment streak: 6 months',
      'Low credit utilisation (50%)',
      'Strong bureau payment index (95)',
      'No public records',
      'All accounts in good standing',
    ],
  },
};

// Scenario 2: Good Customer with Minor Issues
const customer2: CreditScoreDetail = {
  customerId: 'CUST-002',
  customerName: 'TechStart Industries',
  accountNumber: 'ACC-10002',
  internalScore: 690,
  bureauScore: 705,
  scoreVariance: 15,
  riskLevel: 'Low',
  creditLimit: 300000,
  outstandingBalance: 147000,
  utilizationRate: 49,
  paymentBehaviorScore: 82,
  dso: 35,
  bureauLastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
  lastReviewDate: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000),
  bureauSyncStatus: 'synced',
  hasBureauAlerts: true,
  bureauAlertCount: 2,
  scoreHistory: generateScoreHistory(690, 705, 'stable'),
  scoreComponents: {
    paymentHistory: {
      weight: 40,
      score: 85,
      onTimeRate: 95,
      latePaymentFrequency: 1,
      agingBreakdown: {
        current: 95,
        days30: 5,
        days60: 0,
        days90: 0,
        days90Plus: 0,
      },
    },
    creditUtilization: {
      weight: 30,
      score: 82,
      currentUtilization: 49,
      utilizationTrend: 'stable',
    },
    creditHistoryLength: {
      weight: 15,
      score: 78,
      monthsActive: 48,
    },
    paymentBehaviorPatterns: {
      weight: 15,
      score: 80,
      consistency: 85,
      seasonalPatterns: false,
      disputeFrequency: 0,
    },
  },
  bureauIntelligence: {
    bureauCreditScore: 705,
    creditRiskClass: 2,
    paymentIndex: 88,
    daysBeyondTerms: 5,
    publicRecords: [],
    creditInquiries: [
      {
        id: 'INQ-002',
        date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
        requestingCreditor: 'Equipment Leasing Co',
        inquiryType: 'hard',
      },
      {
        id: 'INQ-003',
        date: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000),
        requestingCreditor: 'Business Credit Union',
        inquiryType: 'hard',
      },
    ],
    tradeLineSummary: {
      activeAccounts: 6,
      totalCreditExposure: 1200000,
      averagePaymentDays: 35,
      accountsInGoodStanding: 5,
      accountsWithLatePayments: 1,
    },
    paymentBehaviorTrends: 'Generally pays on time with occasional delays. One 30-day late payment in last 6 months.',
    negativePaymentExperiences: [
      {
        id: 'NPE-001',
        creditorName: 'Office Supplies Inc',
        date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
        daysLate: 30,
        amount: 5000,
        status: 'resolved',
      },
    ],
  },
  bureauEvents: [
    {
      id: 'EVT-002',
      date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
      type: 'inquiry',
      severity: 'info',
      title: 'Credit Inquiry',
      description: 'Hard inquiry from Equipment Leasing Co',
      impact: 'Minor temporary impact on credit score',
    },
    {
      id: 'EVT-003',
      date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
      type: 'late_payment',
      severity: 'warning',
      title: '30-Day Late Payment',
      description: 'Payment to Office Supplies Inc was 30 days late',
      impact: 'Moderate negative impact on payment history',
    },
  ],
  financialMetrics: {
    totalCreditLimit: 300000,
    bureauTotalCreditExposure: 1200000,
    currentOutstandingBalance: 147000,
    availableCredit: 153000,
    averageDaysToPay: 35,
    bureauIndustryAverageDays: 45,
    largestInvoiceOutstanding: 45000,
    totalInvoicesOpen: 8,
    totalInvoicesOverdue: 1,
    bureauTradeLineCount: 6,
  },
  riskIndicators: {
    internalRedFlags: ['1 invoice in 30-day bucket'],
    bureauRedFlags: ['One 30-day late payment in last 6 months'],
    combinedRiskScore: 28,
    positiveIndicators: [
      'Generally pays within terms',
      'Moderate credit utilisation (49%)',
      'Late payment was resolved',
    ],
  },
};

// Scenario 3: Medium Risk - Declining
const customer3: CreditScoreDetail = {
  customerId: 'CUST-003',
  customerName: 'BuildRight Construction',
  accountNumber: 'ACC-10003',
  internalScore: 620,
  bureauScore: 580,
  scoreVariance: -40,
  riskLevel: 'Medium',
  creditLimit: 250000,
  outstandingBalance: 205000,
  utilizationRate: 82,
  paymentBehaviorScore: 65,
  dso: 52,
  bureauLastUpdated: new Date(Date.now() - 8 * 60 * 60 * 1000), // 8 hours ago
  lastReviewDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
  bureauSyncStatus: 'stale',
  hasBureauAlerts: true,
  bureauAlertCount: 3,
  scoreHistory: generateScoreHistory(620, 580, 'declining'),
  scoreComponents: {
    paymentHistory: {
      weight: 40,
      score: 68,
      onTimeRate: 75,
      latePaymentFrequency: 3,
      agingBreakdown: {
        current: 75,
        days30: 10,
        days60: 10,
        days90: 5,
        days90Plus: 0,
      },
    },
    creditUtilization: {
      weight: 30,
      score: 45,
      currentUtilization: 82,
      utilizationTrend: 'increasing',
    },
    creditHistoryLength: {
      weight: 15,
      score: 85,
      monthsActive: 72,
    },
    paymentBehaviorPatterns: {
      weight: 15,
      score: 60,
      consistency: 65,
      seasonalPatterns: true,
      disputeFrequency: 1,
    },
  },
  bureauIntelligence: {
    bureauCreditScore: 580,
    creditRiskClass: 3,
    paymentIndex: 72,
    daysBeyondTerms: 25,
    publicRecords: [],
    creditInquiries: [
      {
        id: 'INQ-004',
        date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
        requestingCreditor: 'Alternative Lender',
        inquiryType: 'hard',
      },
    ],
    tradeLineSummary: {
      activeAccounts: 7,
      totalCreditExposure: 950000,
      averagePaymentDays: 52,
      accountsInGoodStanding: 4,
      accountsWithLatePayments: 3,
    },
    paymentBehaviorTrends: 'Payment performance has deteriorated. Multiple slow payment accounts reported.',
    negativePaymentExperiences: [
      {
        id: 'NPE-002',
        creditorName: 'Materials Supplier LLC',
        date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
        daysLate: 60,
        amount: 35000,
        status: 'ongoing',
      },
      {
        id: 'NPE-003',
        creditorName: 'Equipment Rental Co',
        date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        daysLate: 45,
        amount: 12000,
        status: 'ongoing',
      },
    ],
  },
  bureauEvents: [
    {
      id: 'EVT-004',
      date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
      type: 'late_payment',
      severity: 'warning',
      title: '60-Day Late Payment',
      description: 'Payment to Materials Supplier LLC is 60 days late',
      impact: 'Significant negative impact on credit score',
    },
    {
      id: 'EVT-005',
      date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      type: 'score_change',
      severity: 'warning',
      title: 'Score Decrease',
      description: 'Bureau score decreased by 35 points',
      impact: 'Indicates deteriorating credit profile',
    },
  ],
  financialMetrics: {
    totalCreditLimit: 250000,
    bureauTotalCreditExposure: 950000,
    currentOutstandingBalance: 205000,
    availableCredit: 45000,
    averageDaysToPay: 52,
    bureauIndustryAverageDays: 45,
    largestInvoiceOutstanding: 85000,
    totalInvoicesOpen: 15,
    totalInvoicesOverdue: 6,
    bureauTradeLineCount: 7,
  },
  riskIndicators: {
    internalRedFlags: [
      'High credit utilisation (82%)',
      '2 invoices in 60-day bucket',
      '1 invoice in 90-day bucket',
      'DSO above industry average',
    ],
    bureauRedFlags: [
      '60+ day late payment reported',
      'Multiple slow payment accounts',
      'Payment index declined significantly',
      'Score variance of -40 points',
    ],
    combinedRiskScore: 58,
    positiveIndicators: [
      'Long credit history (72 months)',
    ],
  },
};

// Scenario 4: High Risk - Critical Issues
const customer4: CreditScoreDetail = {
  customerId: 'CUST-004',
  customerName: 'Struggling Retail Co',
  accountNumber: 'ACC-10004',
  internalScore: 520,
  bureauScore: 495,
  scoreVariance: -25,
  riskLevel: 'High',
  creditLimit: 150000,
  outstandingBalance: 114000,
  utilizationRate: 76,
  paymentBehaviorScore: 42,
  dso: 78,
  bureauLastUpdated: new Date(Date.now() - 3 * 60 * 60 * 1000),
  lastReviewDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
  bureauSyncStatus: 'synced',
  hasBureauAlerts: true,
  bureauAlertCount: 5,
  scoreHistory: generateScoreHistory(520, 495, 'declining'),
  scoreComponents: {
    paymentHistory: {
      weight: 40,
      score: 45,
      onTimeRate: 55,
      latePaymentFrequency: 5,
      agingBreakdown: {
        current: 55,
        days30: 15,
        days60: 15,
        days90: 10,
        days90Plus: 5,
      },
    },
    creditUtilization: {
      weight: 30,
      score: 38,
      currentUtilization: 76,
      utilizationTrend: 'increasing',
    },
    creditHistoryLength: {
      weight: 15,
      score: 70,
      monthsActive: 60,
    },
    paymentBehaviorPatterns: {
      weight: 15,
      score: 35,
      consistency: 40,
      seasonalPatterns: false,
      disputeFrequency: 2,
    },
  },
  bureauIntelligence: {
    bureauCreditScore: 495,
    creditRiskClass: 4,
    paymentIndex: 58,
    daysBeyondTerms: 45,
    publicRecords: [
      {
        id: 'PR-001',
        type: 'lien',
        filingDate: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000),
        amount: 75000,
        status: 'active',
        description: 'Tax lien filed by State Revenue Department',
      },
    ],
    creditInquiries: [
      {
        id: 'INQ-005',
        date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        requestingCreditor: 'High-Risk Lender',
        inquiryType: 'hard',
      },
      {
        id: 'INQ-006',
        date: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
        requestingCreditor: 'Emergency Credit Line',
        inquiryType: 'hard',
      },
    ],
    tradeLineSummary: {
      activeAccounts: 5,
      totalCreditExposure: 650000,
      averagePaymentDays: 78,
      accountsInGoodStanding: 1,
      accountsWithLatePayments: 4,
    },
    paymentBehaviorTrends: 'Severe payment difficulties. Multiple 90+ day late payments across trade lines.',
    negativePaymentExperiences: [
      {
        id: 'NPE-004',
        creditorName: 'Wholesale Distributor',
        date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
        daysLate: 90,
        amount: 45000,
        status: 'ongoing',
      },
      {
        id: 'NPE-005',
        creditorName: 'Logistics Provider',
        date: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000),
        daysLate: 95,
        amount: 28000,
        status: 'ongoing',
      },
      {
        id: 'NPE-006',
        creditorName: 'Property Management',
        date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        daysLate: 60,
        amount: 15000,
        status: 'ongoing',
      },
    ],
  },
  bureauEvents: [
    {
      id: 'EVT-006',
      date: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000),
      type: 'public_record',
      severity: 'critical',
      title: 'Tax Lien Filed',
      description: 'CRITICAL: Tax lien filed ($75,000)',
      impact: 'Severe negative impact on creditworthiness',
    },
    {
      id: 'EVT-007',
      date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
      type: 'late_payment',
      severity: 'critical',
      title: '90+ Day Late Payment',
      description: 'Multiple 90+ day late payments reported',
      impact: 'Critical impact on credit score',
    },
    {
      id: 'EVT-008',
      date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
      type: 'account_closure',
      severity: 'critical',
      title: 'Account Closed by Creditor',
      description: 'Trade account closed by creditor due to non-payment',
      impact: 'Severe negative impact',
    },
  ],
  financialMetrics: {
    totalCreditLimit: 150000,
    bureauTotalCreditExposure: 650000,
    currentOutstandingBalance: 114000,
    availableCredit: 36000,
    averageDaysToPay: 78,
    bureauIndustryAverageDays: 45,
    largestInvoiceOutstanding: 52000,
    totalInvoicesOpen: 18,
    totalInvoicesOverdue: 12,
    bureauTradeLineCount: 5,
  },
  riskIndicators: {
    internalRedFlags: [
      '3 consecutive late payments',
      'Very high utilisation (76%)',
      'Multiple invoices in 90+ day bucket',
      'DSO significantly above industry average',
      '5 invoices in 90+ day bucket',
    ],
    bureauRedFlags: [
      'CRITICAL: Tax lien filed ($75,000)',
      'Multiple 90+ day late payments',
      'Account closure by creditor',
      'Payment index critically low (58)',
      'Only 1 of 5 accounts in good standing',
    ],
    combinedRiskScore: 82,
    positiveIndicators: [],
  },
};

// Scenario 5: Good Customer - Expansion Activity
const customer5: CreditScoreDetail = {
  customerId: 'CUST-005',
  customerName: 'GrowthTech Solutions',
  accountNumber: 'ACC-10005',
  internalScore: 710,
  bureauScore: 675,
  scoreVariance: -35,
  riskLevel: 'Low',
  creditLimit: 400000,
  outstandingBalance: 144000,
  utilizationRate: 36,
  paymentBehaviorScore: 88,
  dso: 32,
  bureauLastUpdated: new Date(Date.now() - 1 * 60 * 60 * 1000),
  lastReviewDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
  bureauSyncStatus: 'synced',
  hasBureauAlerts: true,
  bureauAlertCount: 1,
  scoreHistory: generateScoreHistory(710, 675, 'stable'),
  scoreComponents: {
    paymentHistory: {
      weight: 40,
      score: 92,
      onTimeRate: 98,
      latePaymentFrequency: 0,
      agingBreakdown: {
        current: 98,
        days30: 2,
        days60: 0,
        days90: 0,
        days90Plus: 0,
      },
    },
    creditUtilization: {
      weight: 30,
      score: 90,
      currentUtilization: 36,
      utilizationTrend: 'stable',
    },
    creditHistoryLength: {
      weight: 15,
      score: 65,
      monthsActive: 36,
    },
    paymentBehaviorPatterns: {
      weight: 15,
      score: 85,
      consistency: 90,
      seasonalPatterns: true,
      disputeFrequency: 0,
    },
  },
  bureauIntelligence: {
    bureauCreditScore: 675,
    creditRiskClass: 2,
    paymentIndex: 92,
    daysBeyondTerms: 2,
    publicRecords: [],
    creditInquiries: [
      {
        id: 'INQ-007',
        date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        requestingCreditor: 'Commercial Bank A',
        inquiryType: 'hard',
      },
      {
        id: 'INQ-008',
        date: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
        requestingCreditor: 'Equipment Finance Co',
        inquiryType: 'hard',
      },
      {
        id: 'INQ-009',
        date: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
        requestingCreditor: 'Business Credit Line',
        inquiryType: 'hard',
      },
      {
        id: 'INQ-010',
        date: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000),
        requestingCreditor: 'Lease Provider',
        inquiryType: 'hard',
      },
      {
        id: 'INQ-011',
        date: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000),
        requestingCreditor: 'Regional Bank B',
        inquiryType: 'hard',
      },
    ],
    tradeLineSummary: {
      activeAccounts: 9,
      totalCreditExposure: 1800000,
      averagePaymentDays: 32,
      accountsInGoodStanding: 9,
      accountsWithLatePayments: 0,
    },
    paymentBehaviorTrends: 'Excellent payment history. Multiple credit inquiries suggest expansion activity.',
    negativePaymentExperiences: [],
  },
  bureauEvents: [
    {
      id: 'EVT-009',
      date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      type: 'inquiry',
      severity: 'info',
      title: 'Multiple Credit Inquiries',
      description: '5 credit inquiries in last 30 days - indicates expansion activity',
      impact: 'Temporary minor impact on score',
    },
  ],
  financialMetrics: {
    totalCreditLimit: 400000,
    bureauTotalCreditExposure: 1800000,
    currentOutstandingBalance: 144000,
    availableCredit: 256000,
    averageDaysToPay: 32,
    bureauIndustryAverageDays: 45,
    largestInvoiceOutstanding: 38000,
    totalInvoicesOpen: 10,
    totalInvoicesOverdue: 0,
    bureauTradeLineCount: 9,
  },
  riskIndicators: {
    internalRedFlags: [],
    bureauRedFlags: [],
    combinedRiskScore: 22,
    positiveIndicators: [
      'Excellent payment history',
      'Low credit utilisation (36%)',
      'All accounts in good standing',
      'Strong payment index (92)',
      'Expansion activity indicates growth',
    ],
  },
};

// Scenario 6: Medium Risk - Deteriorating
const customer6: CreditScoreDetail = {
  customerId: 'CUST-006',
  customerName: 'Declining Manufacturing Inc',
  accountNumber: 'ACC-10006',
  internalScore: 585,
  bureauScore: 545,
  scoreVariance: -40,
  riskLevel: 'Medium',
  creditLimit: 200000,
  outstandingBalance: 158000,
  utilizationRate: 79,
  paymentBehaviorScore: 58,
  dso: 65,
  bureauLastUpdated: new Date(Date.now() - 10 * 60 * 60 * 1000),
  lastReviewDate: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000),
  bureauSyncStatus: 'stale',
  hasBureauAlerts: true,
  bureauAlertCount: 4,
  scoreHistory: generateScoreHistory(585, 545, 'declining'),
  scoreComponents: {
    paymentHistory: {
      weight: 40,
      score: 62,
      onTimeRate: 70,
      latePaymentFrequency: 4,
      agingBreakdown: {
        current: 70,
        days30: 15,
        days60: 10,
        days90: 5,
        days90Plus: 0,
      },
    },
    creditUtilization: {
      weight: 30,
      score: 42,
      currentUtilization: 79,
      utilizationTrend: 'increasing',
    },
    creditHistoryLength: {
      weight: 15,
      score: 88,
      monthsActive: 96,
    },
    paymentBehaviorPatterns: {
      weight: 15,
      score: 55,
      consistency: 60,
      seasonalPatterns: false,
      disputeFrequency: 1,
    },
  },
  bureauIntelligence: {
    bureauCreditScore: 545,
    creditRiskClass: 3,
    paymentIndex: 68,
    daysBeyondTerms: 35,
    publicRecords: [],
    creditInquiries: [],
    tradeLineSummary: {
      activeAccounts: 6,
      totalCreditExposure: 850000,
      averagePaymentDays: 65,
      accountsInGoodStanding: 3,
      accountsWithLatePayments: 3,
    },
    paymentBehaviorTrends: 'Payment performance has significantly deteriorated over past 6 months.',
    negativePaymentExperiences: [
      {
        id: 'NPE-007',
        creditorName: 'Raw Materials Supplier',
        date: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000),
        daysLate: 75,
        amount: 42000,
        status: 'ongoing',
      },
      {
        id: 'NPE-008',
        creditorName: 'Utilities Provider',
        date: new Date(Date.now() - 55 * 24 * 60 * 60 * 1000),
        daysLate: 60,
        amount: 18000,
        status: 'ongoing',
      },
    ],
  },
  bureauEvents: [
    {
      id: 'EVT-010',
      date: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000),
      type: 'score_change',
      severity: 'critical',
      title: 'Major Score Decline',
      description: 'Score dropped 65 points in 6 months',
      impact: 'Indicates severe deterioration in credit profile',
    },
    {
      id: 'EVT-011',
      date: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000),
      type: 'late_payment',
      severity: 'warning',
      title: '75-Day Late Payment',
      description: 'Payment to Raw Materials Supplier is 75 days late',
      impact: 'Significant negative impact',
    },
  ],
  financialMetrics: {
    totalCreditLimit: 200000,
    bureauTotalCreditExposure: 850000,
    currentOutstandingBalance: 158000,
    availableCredit: 42000,
    averageDaysToPay: 65,
    bureauIndustryAverageDays: 45,
    largestInvoiceOutstanding: 68000,
    totalInvoicesOpen: 14,
    totalInvoicesOverdue: 8,
    bureauTradeLineCount: 6,
  },
  riskIndicators: {
    internalRedFlags: [
      'High credit utilisation (79%)',
      'Multiple invoices in 60+ day bucket',
      'DSO significantly above industry average',
      'Payment behavior declining',
    ],
    bureauRedFlags: [
      'Score dropped 65 points in 6 months',
      'Multiple slow payment accounts',
      'Payment index declined significantly',
      'Only 50% of accounts in good standing',
    ],
    combinedRiskScore: 65,
    positiveIndicators: [
      'Long credit history (96 months)',
    ],
  },
};

// Scenario 7: Excellent Customer - No Issues
const customer7: CreditScoreDetail = {
  customerId: 'CUST-007',
  customerName: 'Premium Services Ltd',
  accountNumber: 'ACC-10007',
  internalScore: 745,
  bureauScore: 760,
  scoreVariance: 15,
  riskLevel: 'Low',
  creditLimit: 350000,
  outstandingBalance: 175000,
  utilizationRate: 50,
  paymentBehaviorScore: 93,
  dso: 25,
  bureauLastUpdated: new Date(Date.now() - 3 * 60 * 60 * 1000),
  lastReviewDate: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
  bureauSyncStatus: 'synced',
  hasBureauAlerts: false,
  bureauAlertCount: 0,
  scoreHistory: generateScoreHistory(745, 760, 'stable'),
  scoreComponents: {
    paymentHistory: {
      weight: 40,
      score: 96,
      onTimeRate: 100,
      latePaymentFrequency: 0,
      agingBreakdown: {
        current: 100,
        days30: 0,
        days60: 0,
        days90: 0,
        days90Plus: 0,
      },
    },
    creditUtilization: {
      weight: 30,
      score: 85,
      currentUtilization: 50,
      utilizationTrend: 'stable',
    },
    creditHistoryLength: {
      weight: 15,
      score: 90,
      monthsActive: 78,
    },
    paymentBehaviorPatterns: {
      weight: 15,
      score: 92,
      consistency: 95,
      seasonalPatterns: false,
      disputeFrequency: 0,
    },
  },
  bureauIntelligence: {
    bureauCreditScore: 760,
    creditRiskClass: 1,
    paymentIndex: 96,
    daysBeyondTerms: 0,
    publicRecords: [],
    creditInquiries: [],
    tradeLineSummary: {
      activeAccounts: 7,
      totalCreditExposure: 2200000,
      averagePaymentDays: 25,
      accountsInGoodStanding: 7,
      accountsWithLatePayments: 0,
    },
    paymentBehaviorTrends: 'Consistently excellent payment performance across all trade lines.',
    negativePaymentExperiences: [],
  },
  bureauEvents: [
    {
      id: 'EVT-012',
      date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
      type: 'payment_improvement',
      severity: 'positive',
      title: 'Consistent Excellence',
      description: 'Maintained perfect payment record for 12 consecutive months',
      impact: 'Positive reinforcement of creditworthiness',
    },
  ],
  financialMetrics: {
    totalCreditLimit: 350000,
    bureauTotalCreditExposure: 2200000,
    currentOutstandingBalance: 175000,
    availableCredit: 175000,
    averageDaysToPay: 25,
    bureauIndustryAverageDays: 45,
    largestInvoiceOutstanding: 55000,
    totalInvoicesOpen: 9,
    totalInvoicesOverdue: 0,
    bureauTradeLineCount: 7,
  },
  riskIndicators: {
    internalRedFlags: [],
    bureauRedFlags: [],
    combinedRiskScore: 12,
    positiveIndicators: [
      'Perfect payment record',
      'Moderate credit utilisation (50%)',
      'Strong bureau payment index (96)',
      'All accounts in good standing',
      'Pays well ahead of industry average',
    ],
  },
};

// Scenario 8: Critical Risk
const customer8: CreditScoreDetail = {
  customerId: 'CUST-008',
  customerName: 'Crisis Enterprises',
  accountNumber: 'ACC-10008',
  internalScore: 480,
  bureauScore: 465,
  scoreVariance: -15,
  riskLevel: 'Critical',
  creditLimit: 100000,
  outstandingBalance: 98000,
  utilizationRate: 98,
  paymentBehaviorScore: 28,
  dso: 95,
  bureauLastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000),
  lastReviewDate: new Date(Date.now() - 70 * 24 * 60 * 60 * 1000),
  bureauSyncStatus: 'synced',
  hasBureauAlerts: true,
  bureauAlertCount: 7,
  scoreHistory: generateScoreHistory(480, 465, 'declining'),
  scoreComponents: {
    paymentHistory: {
      weight: 40,
      score: 32,
      onTimeRate: 40,
      latePaymentFrequency: 8,
      agingBreakdown: {
        current: 40,
        days30: 20,
        days60: 20,
        days90: 15,
        days90Plus: 5,
      },
    },
    creditUtilization: {
      weight: 30,
      score: 15,
      currentUtilization: 98,
      utilizationTrend: 'increasing',
    },
    creditHistoryLength: {
      weight: 15,
      score: 75,
      monthsActive: 54,
    },
    paymentBehaviorPatterns: {
      weight: 15,
      score: 25,
      consistency: 30,
      seasonalPatterns: false,
      disputeFrequency: 3,
    },
  },
  bureauIntelligence: {
    bureauCreditScore: 465,
    creditRiskClass: 5,
    paymentIndex: 45,
    daysBeyondTerms: 65,
    publicRecords: [
      {
        id: 'PR-002',
        type: 'judgment',
        filingDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
        amount: 125000,
        status: 'active',
        description: 'Court judgment for unpaid vendor invoices',
      },
    ],
    creditInquiries: [
      {
        id: 'INQ-012',
        date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        requestingCreditor: 'Last Resort Funding',
        inquiryType: 'hard',
      },
    ],
    tradeLineSummary: {
      activeAccounts: 4,
      totalCreditExposure: 450000,
      averagePaymentDays: 95,
      accountsInGoodStanding: 0,
      accountsWithLatePayments: 4,
    },
    paymentBehaviorTrends: 'Critical payment difficulties. All trade lines showing severe delinquency.',
    negativePaymentExperiences: [
      {
        id: 'NPE-009',
        creditorName: 'Major Supplier A',
        date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
        daysLate: 120,
        amount: 85000,
        status: 'ongoing',
      },
      {
        id: 'NPE-010',
        creditorName: 'Service Provider B',
        date: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000),
        daysLate: 105,
        amount: 52000,
        status: 'ongoing',
      },
      {
        id: 'NPE-011',
        creditorName: 'Vendor C',
        date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
        daysLate: 90,
        amount: 38000,
        status: 'ongoing',
      },
    ],
  },
  bureauEvents: [
    {
      id: 'EVT-013',
      date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
      type: 'public_record',
      severity: 'critical',
      title: 'Court Judgment Filed',
      description: 'CRITICAL: Court judgment for $125,000',
      impact: 'Severe negative impact on creditworthiness',
    },
    {
      id: 'EVT-014',
      date: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000),
      type: 'late_payment',
      severity: 'critical',
      title: 'Multiple 90+ Day Late Payments',
      description: 'All active accounts showing 90+ day delinquency',
      impact: 'Critical impact on credit score',
    },
    {
      id: 'EVT-015',
      date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
      type: 'account_closure',
      severity: 'critical',
      title: 'Multiple Account Closures',
      description: '3 trade accounts closed by creditors',
      impact: 'Severe negative impact',
    },
  ],
  financialMetrics: {
    totalCreditLimit: 100000,
    bureauTotalCreditExposure: 450000,
    currentOutstandingBalance: 98000,
    availableCredit: 2000,
    averageDaysToPay: 95,
    bureauIndustryAverageDays: 45,
    largestInvoiceOutstanding: 45000,
    totalInvoicesOpen: 22,
    totalInvoicesOverdue: 18,
    bureauTradeLineCount: 4,
  },
  riskIndicators: {
    internalRedFlags: [
      'Critical credit utilisation (98%)',
      'Multiple invoices in 90+ day bucket',
      'DSO critically above industry average',
      'Payment behavior critically poor',
      '18 of 22 invoices overdue',
    ],
    bureauRedFlags: [
      'CRITICAL: Court judgment filed ($125,000)',
      'All accounts showing severe delinquency',
      'Multiple 90+ day late payments',
      'Multiple account closures by creditors',
      'Payment index critically low (45)',
      'Zero accounts in good standing',
    ],
    combinedRiskScore: 95,
    positiveIndicators: [],
  },
};

// Convert to CreditScore array (without detail fields)
export const mockCreditScores: CreditScore[] = [
  customer1,
  customer2,
  customer3,
  customer4,
  customer5,
  customer6,
  customer7,
  customer8,
].map(({ scoreHistory, scoreComponents, bureauIntelligence, bureauEvents, financialMetrics, riskIndicators, ...rest }) => {
  // These properties are intentionally excluded from the summary list
  void scoreHistory;
  void scoreComponents;
  void bureauIntelligence;
  void bureauEvents;
  void financialMetrics;
  void riskIndicators;
  return rest;
});

// Full details map
export const mockCreditScoreDetails: Record<string, CreditScoreDetail> = {
  'CUST-001': customer1,
  'CUST-002': customer2,
  'CUST-003': customer3,
  'CUST-004': customer4,
  'CUST-005': customer5,
  'CUST-006': customer6,
  'CUST-007': customer7,
  'CUST-008': customer8,
};

// Dashboard metrics
export const mockDashboardMetrics: CreditScoringDashboardMetrics = {
  averageScore: 648,
  highRiskAccountsCount: 2,
  recentScoreChanges: 4,
  pendingReviews: 3,
  bureauSyncStatus: {
    lastSync: new Date(Date.now() - 30 * 60 * 1000), // 30 min ago
    syncHealth: 'healthy',
    pendingUpdates: 2,
  },
  scoreDistribution: {
    excellent: 2, // 750+
    good: 3,      // 650-749
    fair: 2,      // 550-649
    poor: 1,      // <550
  },
};

// Bureau alerts
export const mockBureauAlerts: BureauAlert[] = [
  {
    id: 'ALERT-001',
    customerId: 'CUST-004',
    customerName: 'Struggling Retail Co',
    severity: 'critical',
    type: 'public_record',
    title: 'Tax Lien Filed',
    description: 'CRITICAL: Tax lien filed for $75,000 by State Revenue Department',
    date: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000),
    reviewed: false,
  },
  {
    id: 'ALERT-002',
    customerId: 'CUST-006',
    customerName: 'Declining Manufacturing Inc',
    severity: 'critical',
    type: 'score_change',
    title: 'Major Score Decline',
    description: 'Bureau score dropped 65 points in 6 months',
    date: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000),
    reviewed: false,
  },
  {
    id: 'ALERT-003',
    customerId: 'CUST-003',
    customerName: 'BuildRight Construction',
    severity: 'warning',
    type: 'late_payment',
    title: '60-Day Late Payment',
    description: 'Payment to Materials Supplier LLC is 60 days late',
    date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
    reviewed: false,
  },
  {
    id: 'ALERT-004',
    customerId: 'CUST-008',
    customerName: 'Crisis Enterprises',
    severity: 'critical',
    type: 'public_record',
    title: 'Court Judgment',
    description: 'Court judgment filed for $125,000',
    date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
    reviewed: true,
    assignedTo: 'John Smith',
  },
  {
    id: 'ALERT-005',
    customerId: 'CUST-005',
    customerName: 'GrowthTech Solutions',
    severity: 'info',
    type: 'inquiry',
    title: 'Multiple Credit Inquiries',
    description: '5 credit inquiries in last 30 days - indicates expansion activity',
    date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    reviewed: true,
  },
  {
    id: 'ALERT-006',
    customerId: 'CUST-002',
    customerName: 'TechStart Industries',
    severity: 'warning',
    type: 'late_payment',
    title: '30-Day Late Payment',
    description: 'Payment to Office Supplies Inc was 30 days late',
    date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
    reviewed: true,
  },
  {
    id: 'ALERT-007',
    customerId: 'CUST-001',
    customerName: 'Acme Corporation',
    severity: 'positive',
    type: 'payment_improvement',
    title: 'Perfect Payment Streak',
    description: '90 consecutive days of on-time payments',
    date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
    reviewed: true,
  },
];
